import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { makeStyles } from 'design-system/styles';
import { Typography } from 'react-scale/components/design-system/product';
import classnames from 'classnames';
import { ModelInputFileAttachmentType } from '@scale/shared/types/chat';
import { Modal, Button, Spinner } from '@scale/scaleui';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { checkImageOriginalityResponse } from '@scale/mm-shared';
import {
  getFileType,
  isAllowedMimeType,
} from 'react-scale/components/scalers/tasks/textcollection/fileUploadUtils';

type StyleProps = { isDragActive: boolean };
type Props = {
  allowedMimeTypesByInputType: Record<ModelInputFileAttachmentType, string[]>;
  onFiles: (file: File[]) => void;
  setError: (error: string) => void;
  totalFileSizeLimitInBytes: number;
  checkImageOriginality: (image: File) => Promise<{ data: checkImageOriginalityResponse }>;
  projectId: string | undefined;
  multipleFiles?: boolean;
};

const FilesUpload: React.FC<Props> = ({
  allowedMimeTypesByInputType,
  onFiles,
  setError,
  totalFileSizeLimitInBytes,
  checkImageOriginality,
  projectId,
  multipleFiles,
}) => {
  const [unoriginalFiles, setUnoriginalFiles] = useState<File[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const shouldCheckImageOriginality = useUnleashFeatureFlag(
    'multimodal-check-image-originality',
    { projectId },
    false,
  );

  const onDrop = useCallback(
    async (files: File[]) => {
      setError('');
      setUnoriginalFiles([]);
      setIsLoading(true);
      if (files && files.length > 0) {
        if (!areFilesMimeTypesAllowed(files, allowedMimeTypesByInputType)) {
          const allowedMimeTypes = Object.values(allowedMimeTypesByInputType).flat().join(',');
          setError(`You can only upload files of the following types: ${allowedMimeTypes}`);
          setIsLoading(false);
          return;
        }

        if (shouldCheckImageOriginality) {
          const originalFiles: File[] = [];
          const unoriginalFiles: File[] = [];

          for (const file of files) {
            const response = await checkImageOriginality(file);
            if (response) {
              if (response.data && response.data.isOriginal) {
                originalFiles.push(file);
              } else {
                unoriginalFiles.push(file);
              }
            }
          }

          if (originalFiles.length > 0) {
            onFiles(originalFiles);
          }

          if (unoriginalFiles.length > 0) {
            setUnoriginalFiles(unoriginalFiles);
            setIsModalOpen(true);
          }
        } else {
          onFiles(files);
        }

        setIsLoading(false);
      }
    },
    [
      setError,
      allowedMimeTypesByInputType,
      onFiles,
      checkImageOriginality,
      shouldCheckImageOriginality,
    ],
  );

  const handleModalClose = () => {
    setUnoriginalFiles([]);
    setIsModalOpen(false);
  };

  const handleAllowUnoriginal = () => {
    onFiles(unoriginalFiles);
    setUnoriginalFiles([]);
    setIsModalOpen(false);
  };

  const areFilesMimeTypesAllowed = (
    files: File[],
    allowedMimeTypesByInputType: Record<ModelInputFileAttachmentType, string[]>,
  ) => {
    return files.every(file => {
      const fileType = getFileType(file);
      if (!fileType) {
        return false;
      }
      if (allowedMimeTypesByInputType[fileType].length === 0) {
        return false;
      }
      return isAllowedMimeType(file.type, allowedMimeTypesByInputType[fileType]);
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });
  const classes = useStyles({ isDragActive });

  return (
    <div className={classes.container}>
      <div
        {...getRootProps()}
        className={classnames(classes.dropzone, 'bg-neutral-100', {
          [classes.disabled]: isLoading,
        })}
      >
        <input
          type="file"
          {...getInputProps()}
          accept={Object.values(allowedMimeTypesByInputType).flat().join(',')}
          multiple={multipleFiles ?? false}
        />
        <img src="/static/img/product/upload-cyclops.svg" alt="" />
        <div className={classes.desc} style={{ whiteSpace: 'pre-wrap' }}>
          <Typography>Drag and drop here or click to upload your data</Typography>
        </div>
        {isLoading && (
          <div className={classes.spinnerOverlay}>
            <Spinner size="md" />
          </div>
        )}
      </div>
      <div className={classes.footer}>
        {Object.values(allowedMimeTypesByInputType).flat().length > 0 && (
          <span>
            Supported file types: {Object.values(allowedMimeTypesByInputType).flat().join(',')}
          </span>
        )}
        <br />
        {totalFileSizeLimitInBytes && (
          <span>Max total file size: {totalFileSizeLimitInBytes / 1000000}mb</span>
        )}
      </div>
      {isModalOpen && (
        <Modal onClose={handleModalClose} title="Unoriginal Files Detected">
          <Typography>
            Some files were flagged as unoriginal. Do you certify that these are your own files and
            you want to proceed?
          </Typography>
          <div className={classes.modalActions}>
            <Button onClick={handleModalClose} variant="secondary">
              No
            </Button>
            <Button onClick={handleAllowUnoriginal} variant="primary">
              Yes
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const useStyles = makeStyles<StyleProps>(theme => ({
  container: {
    position: 'relative',
  },
  dropzone: props => ({
    width: '100%',
    height: '300px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    borderRadius: theme.borderRadius,
    border: props.isDragActive
      ? `1px dashed ${theme.colors.primary1}`
      : '1px solid rgba(0, 0, 0, 0)',
    cursor: 'pointer',
    transition: 'all 0.2s',
    position: 'relative',

    '&:hover': {
      border: `1px dashed ${theme.colors.primary1}`,
    },
    padding: theme.spacing(0.5),
  }),
  desc: {
    marginTop: theme.spacing(3),
    color: theme.colors.gray50,
  },
  footer: {
    marginTop: theme.spacing(2),
    color: theme.colors.gray50,
    textAlign: 'center',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
    gap: theme.spacing(2),
  },
  spinnerOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    zIndex: 10,
  },
  disabled: {
    pointerEvents: 'none',
  },
}));

export default FilesUpload;
